import {
  IconBellRinging,
  IconBuildingStore,
  IconCalendarEvent,
  IconCash,
  IconChartBar,
  IconCoin,
  IconHistory,
  IconLayoutDashboard,
  IconReceipt,
  IconSettings,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import type { StaffRole } from '../../api-client.ts';

/**
 * Sidebar nav tree — pure data. Zero React hooks, zero state, zero
 * persistence. Anything that reads role / profile / localStorage lives
 * in callers (useSidebarFoldState / Sidebar component).
 *
 * Tree is 1-level: leaves or groups-of-leaves. Nested groups (>1 level)
 * are intentionally disallowed — past that depth a flat search UI beats
 * a tree.
 */

export interface NavLeaf {
  readonly type: 'leaf';
  readonly to: string;
  readonly label: string;
  readonly icon: (size: number) => JSX.Element;
  readonly minRole: StaffRole;
}

export interface NavGroup {
  readonly type: 'group';
  readonly key: string;
  readonly label: string;
  readonly icon: (size: number) => JSX.Element;
  readonly children: readonly NavLeaf[];
  /** Default expand state on first render (before localStorage override). */
  readonly defaultOpen: boolean;
}

export type NavItem = NavLeaf | NavGroup;

const RANK: Record<StaffRole, number> = { staff: 1, manager: 2, admin: 3 };

export function roleMeets(actual: StaffRole, required: StaffRole): boolean {
  return RANK[actual] >= RANK[required];
}

/** Group visible iff any child passes the role gate. */
export function groupVisible(g: NavGroup, role: StaffRole): boolean {
  return g.children.some((c) => roleMeets(role, c.minRole));
}

export const ROLE_BADGE: Record<StaffRole, { color: string; label: string }> = {
  admin: { color: 'grape', label: 'ADMIN' },
  manager: { color: 'blue', label: 'MANAGER' },
  staff: { color: 'teal', label: 'STAFF' },
};

/**
 * Factory returns an immutable tree. `notificationsEnabled` is the only
 * runtime knob (capability-gated leaf in 客戶 group). Other gates should
 * be added the same way — as factory parameters, never as `if` inside
 * callers.
 */
export function buildNavTree(notificationsEnabled: boolean): readonly NavItem[] {
  return [
    {
      type: 'leaf',
      to: '/',
      label: '概覽',
      icon: (s) => <IconLayoutDashboard size={s} />,
      minRole: 'staff',
    },
    {
      type: 'group',
      key: 'ops',
      label: '營業',
      icon: (s) => <IconReceipt size={s} />,
      defaultOpen: true,
      children: [
        {
          type: 'leaf',
          to: '/reservations',
          label: '訂位',
          icon: (s) => <IconCalendarEvent size={s} />,
          minRole: 'staff',
        },
        {
          type: 'leaf',
          to: '/pos',
          label: 'POS',
          icon: (s) => <IconReceipt size={s} />,
          minRole: 'staff',
        },
        {
          type: 'leaf',
          to: '/analytics',
          label: '訂位分析',
          icon: (s) => <IconChartBar size={s} />,
          minRole: 'manager',
        },
      ],
    },
    {
      type: 'group',
      key: 'payments',
      label: '金流',
      icon: (s) => <IconCash size={s} />,
      defaultOpen: false,
      children: [
        {
          type: 'leaf',
          to: '/payments',
          label: '金流總覽',
          icon: (s) => <IconChartBar size={s} />,
          minRole: 'manager',
        },
        {
          type: 'leaf',
          to: '/payments/charges',
          label: '交易明細',
          icon: (s) => <IconReceipt size={s} />,
          minRole: 'manager',
        },
        {
          type: 'leaf',
          to: '/payments/refunds',
          label: '退款管理',
          icon: (s) => <IconHistory size={s} />,
          minRole: 'manager',
        },
        {
          type: 'leaf',
          to: '/payments/expenses',
          label: '成本管理',
          icon: (s) => <IconCoin size={s} />,
          minRole: 'manager',
        },
        {
          type: 'leaf',
          to: '/payments/gateways',
          label: '金流商管理',
          icon: (s) => <IconSettings size={s} />,
          minRole: 'admin',
        },
      ],
    },
    {
      type: 'group',
      key: 'customers',
      label: '客戶',
      icon: (s) => <IconUsers size={s} />,
      defaultOpen: false,
      children: [
        {
          type: 'leaf',
          to: '/customers',
          label: '客戶名單',
          icon: (s) => <IconUsers size={s} />,
          minRole: 'manager',
        },
        ...(notificationsEnabled
          ? [
              {
                type: 'leaf' as const,
                to: '/notifications',
                label: '通知紀錄',
                icon: (s: number) => <IconBellRinging size={s} />,
                minRole: 'manager' as StaffRole,
              },
            ]
          : []),
      ],
    },
    {
      type: 'group',
      key: 'people',
      label: '人員',
      icon: (s) => <IconUsersGroup size={s} />,
      defaultOpen: false,
      children: [
        {
          type: 'leaf',
          to: '/staff',
          label: '員工管理',
          icon: (s) => <IconUsersGroup size={s} />,
          minRole: 'admin',
        },
        {
          type: 'leaf',
          to: '/audit',
          label: '操作稽核',
          icon: (s) => <IconHistory size={s} />,
          minRole: 'manager',
        },
      ],
    },
    {
      type: 'group',
      key: 'system',
      label: '系統設定',
      icon: (s) => <IconBuildingStore size={s} />,
      defaultOpen: false,
      children: [
        {
          type: 'leaf',
          to: '/settings',
          label: '場館設定',
          icon: (s) => <IconSettings size={s} />,
          minRole: 'admin',
        },
      ],
    },
  ];
}

/** Pure: filter tree by role; leaves invisible leaves/groups out entirely. */
export function filterVisibleTree(
  tree: readonly NavItem[],
  role: StaffRole,
): readonly NavItem[] {
  return tree
    .filter((n) => (n.type === 'leaf' ? roleMeets(role, n.minRole) : groupVisible(n, role)))
    .map<NavItem>((n) =>
      n.type === 'group'
        ? {
            ...n,
            children: n.children.filter((c) => roleMeets(role, c.minRole)),
          }
        : n,
    );
}

/**
 * Loose match — the leaf's `to` is a prefix of (or equal to) the current
 * path. Used for the GROUP-level "has active child" decoration — a group
 * remains highlighted while user is anywhere under it (e.g. /payments/*).
 */
export function pathMatches(to: string, current: string): boolean {
  if (to === '/') return current === '/';
  return current === to || current.startsWith(`${to}/`);
}

/**
 * Pick exactly one leaf to mark as active — the one whose `to` is the
 * longest prefix-or-exact match of the current path. Fixes the
 * "金流總覽 底色不消失" bug: previously `/payments` leaf prefix-matched
 * every /payments/* sub-page so it stayed highlighted alongside the
 * actual sub-page leaf. With this picker, only the most specific leaf
 * is active.
 */
export function pickActiveLeafPath(
  tree: readonly NavItem[],
  current: string,
): string | null {
  const leaves: NavLeaf[] = [];
  for (const n of tree) {
    if (n.type === 'leaf') leaves.push(n);
    else leaves.push(...n.children);
  }
  let bestTo: string | null = null;
  let bestLen = -1;
  for (const leaf of leaves) {
    if (!pathMatches(leaf.to, current)) continue;
    if (leaf.to.length > bestLen) {
      bestLen = leaf.to.length;
      bestTo = leaf.to;
    }
  }
  return bestTo;
}
